import React, { useCallback, useEffect, useState } from "react";
import { FaClipboardList } from "react-icons/fa";
import {
  BookingSegment, BookingTraveller, ExistingSsr, SsrCatalogue, SsrOption,
  addBookingSsrs, addFrequentFlyer, getBookingSsrs, getSsrCatalogue,
} from "../../services/api";

/**
 * Special requests on a booking that has already been ticketed.
 *
 * Before this, an SSR could only be attached while the PNR was being created —
 * a passenger who wanted a vegetarian meal the day after they booked had no
 * route, and neither did the agent taking the call.
 *
 * Its own component rather than more of BookingHistory, which is already 1,250
 * lines and carries the cancel and exchange flows.
 *
 * Only meals, mobility assistance and seat preference can be changed here.
 * Passport and contact details are captured during booking; editing identity
 * data on a ticketed sale is a heavier action that deserves its own screen.
 */

/**
 * What the airline made of a request.
 *
 * Shown honestly, and this is the point of the panel: an SSR is a REQUEST. A
 * window seat comes back UN from carriers that will not hold one, and an agent
 * who told the passenger "done" needs to see that.
 */
function statusChip(status?: string | null): { label: string; className: string } {
  const s = (status ?? "").toUpperCase();
  if (["KK", "HK", "HN"].includes(s)) return { label: "Confirmed", className: "bg-green-50 text-green-700 border-green-200" };
  if (["UC", "UN", "NO", "XX"].includes(s)) return { label: "Not available", className: "bg-red-50 text-red-700 border-red-200" };
  if (["NN", "PN"].includes(s)) return { label: "Awaiting airline", className: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: s || "—", className: "bg-gray-50 text-gray-500 border-gray-200" };
}

const SpecialRequestsPanel: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const [services, setServices] = useState<ExistingSsr[]>([]);
  const [travellers, setTravellers] = useState<BookingTraveller[]>([]);
  const [segments, setSegments] = useState<BookingSegment[]>([]);
  const [who, setWho] = useState("");
  const [catalogue, setCatalogue] = useState<SsrCatalogue>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [choice, setChoice] = useState("");
  const [ffCarrier, setFfCarrier] = useState("");
  const [ffNumber, setFfNumber] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [current, cat] = await Promise.all([getBookingSsrs(bookingId), getSsrCatalogue()]);
      setServices(current.services ?? []);
      setTravellers(current.travellers ?? []);
      setSegments(current.segments ?? []);
      // Default to the first passenger, but never silently: the picker shows
      // who it is whenever there is more than one.
      setWho((prev) => prev || current.travellers?.[0]?.nameNumber || "1.1");
      setCatalogue(cat);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.errors?.[0]?.message ?? "Could not read the airline's requests.");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const addable: SsrOption[] = [
    ...(catalogue.meal ?? []),
    ...(catalogue.assistance ?? []),
    ...(catalogue.seat_preference ?? []),
  ];

  const add = async () => {
    if (!choice || busy) return;
    setBusy(true);
    setError("");
    setNote("");
    try {
      // Every leg, not just the first. A meal is per segment, and a return
      // trip that gets one outbound and nothing coming home is the bug this
      // replaces. The server ignores segments for journey-wide codes.
      const r = await addBookingSsrs(bookingId, [{
        code: choice,
        nameNumber: who || "1.1",
        segmentNumbers: segments.map((sg) => String(sg.number)),
      }]);
      const skipped = r.skipped?.length ? ` ${r.skipped.join(", ")} was already there.` : "";
      setNote((r.message ?? "Sent.") + skipped);
      setChoice("");
      await load();
    } catch (e: any) {
      // The airline's own words. It says which entry it objected to, and that
      // is the only useful thing to show.
      setError(e?.response?.data?.errors?.[0]?.message ?? "The airline would not accept that request.");
    } finally {
      setBusy(false);
    }
  };

  const addFf = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNote("");
    try {
      const r = await addFrequentFlyer(bookingId, ffCarrier.trim(), ffNumber.trim());
      setNote(r.message);
      setFfCarrier("");
      setFfNumber("");
      await load();
    } catch (e: any) {
      // The host's own words. It refuses a number that belongs to somebody
      // else by saying exactly that, and no wording of ours would be clearer.
      setError(e?.response?.data?.errors?.[0]?.message ?? "That number was not accepted.");
    } finally {
      setBusy(false);
    }
  };

  // SSRs the airline adds itself — ticketing deadlines, its own notices. Real,
  // but not something an agent acts on here, and they crowd out what is.
  const shown = services.filter((s) => !["ADTK", "OTHS"].includes((s.code ?? "").toUpperCase()));

  return (
    <div className="px-5 py-4 border-b border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <FaClipboardList size={12} className="text-primary" />
        <h4 className="text-sm font-bold text-gray-800">Special Requests</h4>
      </div>

      {loading ? (
        <div className="text-xs text-gray-400">Reading from the airline…</div>
      ) : (
        <>
          {shown.length === 0 ? (
            <div className="text-xs text-gray-400 mb-3">Nothing requested on this booking yet.</div>
          ) : (
            <div className="space-y-2 mb-3">
              {shown.map((s, i) => {
                const chip = statusChip(s.status);
                return (
                  <div key={`${s.code}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-white rounded-lg px-4 py-2.5 border border-gray-100">
                    <span className="font-mono text-xs font-bold text-gray-700">{s.code}</span>
                    <span className="text-sm text-gray-900 flex-1 min-w-0 truncate">{s.label ?? s.code}</span>
                    {/* Who and where, so an agent can see at a glance that the
                        return leg was missed or a second passenger has none. */}
                    {travellers.length > 1 && s.travellers.length > 0 && (
                      <span className="text-[11px] text-gray-500">
                        {s.travellers
                          .map((n) => travellers.find((t) => t.number === n)?.name ?? `Passenger ${n}`)
                          .join(", ")}
                      </span>
                    )}
                    {segments.length > 1 && s.segments.length > 0 && (
                      <span className="text-[11px] text-gray-400">
                        {s.segments
                          .map((n) => {
                            const sg = segments.find((x) => x.number === n);
                            return sg ? `${sg.from}–${sg.to}` : `leg ${n}`;
                          })
                          .join(", ")}
                      </span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${chip.className}`}>
                      {chip.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {travellers.length > 1 && (
              <select
                value={who}
                onChange={(e) => { setWho(e.target.value); setNote(""); setError(""); }}
                disabled={busy}
                className="rounded-lg border border-gray-300 px-3 py-2 text-xs bg-white"
                aria-label="Passenger"
              >
                {travellers.map((t) => (
                  <option key={t.nameNumber} value={t.nameNumber}>{t.name}</option>
                ))}
              </select>
            )}
            <select
              value={choice}
              onChange={(e) => { setChoice(e.target.value); setNote(""); setError(""); }}
              disabled={busy}
              className="rounded-lg border border-gray-300 px-3 py-2 text-xs bg-white min-w-[14rem]"
              aria-label="Add a special request"
            >
              <option value="">Add a request…</option>
              {addable.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={add}
              disabled={!choice || busy}
              className="rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Add"}
            </button>
            <button
              type="button"
              onClick={load}
              disabled={busy || loading}
              className="text-xs text-gray-500 hover:underline disabled:opacity-50"
            >
              Refresh status
            </button>
          </div>

          {/* ── Frequent flyer ──────────────────────────────────────────
              Its own row because it is its own mechanism: no API element
              exists, so the server sends the FF host entry. Sabre checks the
              number with the airline as it goes in. */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <input
              type="text"
              value={ffCarrier}
              onChange={(e) => { setFfCarrier(e.target.value.toUpperCase().slice(0, 2)); setNote(""); setError(""); }}
              placeholder="EY"
              maxLength={2}
              disabled={busy}
              aria-label="Airline code"
              className="w-16 rounded-lg border border-gray-300 px-3 py-2 text-xs uppercase"
            />
            <input
              type="text"
              value={ffNumber}
              onChange={(e) => { setFfNumber(e.target.value.toUpperCase()); setNote(""); setError(""); }}
              placeholder="Frequent flyer number"
              disabled={busy}
              aria-label="Frequent flyer number"
              className="rounded-lg border border-gray-300 px-3 py-2 text-xs min-w-[12rem]"
            />
            <button
              type="button"
              onClick={addFf}
              disabled={busy || ffCarrier.length !== 2 || ffNumber.trim().length < 4}
              className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Add number
            </button>
          </div>

          {note && <p className="mt-2 text-xs text-green-700">{note}</p>}
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          <p className="mt-2 text-[11px] text-gray-400">
            Requests are confirmed by the airline, not by us. Meals and assistance
            only — seats and bags need a paid document we do not issue.
          </p>
        </>
      )}
    </div>
  );
};

export default SpecialRequestsPanel;
